import React, { useState, useEffect } from 'react';
import Helmet from 'react-helmet';
import moment from 'moment'
import EditGoals from '../components/EditGoals'
import BarGraph from 'components/shared/graphs/BarGraph'
import { revenueData, revenueOptions } from '../utils/revenue'
import { costPerPurchaseData, costPerPurchaseOptions } from '../utils/costPerPurchase'
import { adspendData, adspendOptions } from '../utils/adspend'
import { roasData, roasOptions } from '../utils/roas'
import { aovData, aovOptions } from '../utils/aov'
import { shortFormatCurrency } from 'lib/utils/currency';
import { messages, cppMessages } from '../utils/messages';
import Button from 'components/shared/app/Button';
import Config from 'lib/Config';
import { ManagedRequest, RequestStatus } from 'lib/RequestManager';
import { ErrorMessages } from 'lib/AppErrors';
import { PageHelper } from 'lib/helpers/PageHelper';
import { CampaignDataFilter } from 'lib/data/models/CampaignObjects';
import { createIsoTimeRange, DAYS, now, daysInThisMonth, thisMonthSoFarLength } from 'utils/DateUtils.js';
import { ProviderNames } from 'lib/data/models/Providers';
import { Goal } from 'lib/data/models/Goals';
import { calculateMetricsAcrossDays } from 'utils/adaya/ProviderUtils';
import { findSuggestedMax } from 'components/shared/graphs/utils';
import { getAccountProviders, getInnerError, renderError, renderLoader, getGoal } from 'utils/adaya/AdayaUtils';
import { useLocation, useNavigate } from 'react-router-dom';
import { getLogger } from 'lib/utils/logging';
import { orZero } from 'lib/utils/NumUtils';
import './style.scss';

const pageHelper = new PageHelper();
const { log } = getLogger('GoalTracker', { enabled: false });

const GoalMobile = (props) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(undefined);

    const [tab, setTab] = useState(undefined);
    const [previousTab, setPreviousTab] = useState('');
    const [dataReady, setDataReady] = useState(false);
    const [editingRevenueGoal, setRevenueGoal] = useState(0);
    const [editingCppGoal, setCppGoal] = useState(0);
    const [editingAovGoal, setAovGoal] = useState(0);
    const [editingMerGoal, setMerGoal] = useState(0);
    const [editingRoasGoal, setRoasGoal] = useState(0);

    const {auth} = props;

    const user = auth?.user;
    const account = user?.activeAccount;

    const currentMonth = ''

    const [processedData, setProcessedData] = useState(undefined);
    const [processedROASData, setProcessedROASData] = useState(undefined);
    const [currentMonthData, setCurrentMonthData] = useState(undefined);
    const [monthProjectedRevenue, setMonthProjectedRevenue] = useState(0);
    const [monthProjectedAdspend, setMonthProjectedAdspend] = useState(0);
    const [monthProjectedMer, setMonthProjectedMer] = useState(0);
    const [monthProjectedAov, setMonthProjectedAov] = useState(0);
    const [monthProjectedCpp, setMonthProjectedCpp] = useState(0);
    const [monthRevenueSuggestedMax, setMonthRevenueSuggestedMax] = useState(0);
    const [monthAdspendSuggestedMax, setMonthAdspendSuggestedMax] = useState(0);
    const [monthMerSuggestedMax, setMonthMerSuggestedMax] = useState(0);
    const [monthCppSuggestedMax, setMonthCppSuggestedMax] = useState(0);
    const [monthAovSuggestedMax, setMonthAovSuggestedMax] = useState(0);

    const location = useLocation();
    const navigate = useNavigate();

    // todo: get goal from GoalHelper
    const daysInMonth = daysInThisMonth();
    const merGoal = getGoal(account, Goal.Mer);
    const revenueGoal = getGoal(account, Goal.Revenue);
    const aovGoal = getGoal(account, Goal.Aov);
    const roasGoal = getGoal(account, Goal.Roas);
    const cppGoal = getGoal(account, Goal.Cpp);
    const adspendGoal = (roasGoal == 0) ? 0 : orZero((revenueGoal / merGoal).toFixed(2));

    useEffect(async () => {
        await fetchAllData();
    }, [user]);

    useEffect(() => {
        if (location.pathname == '/goals/edit') {
            setTab('edit-goals');
            setPreviousTab('revenue');
        } else if (location.pathname == '/goals') {
            setTab('revenue');
            setPreviousTab(tab);
        }
    }, [location]);

    // data is changed, ready
    useEffect(() => {
        if (processedData && currentMonthData) {
            const _thisMonthSoFarLength = thisMonthSoFarLength();
            setMonthProjectedRevenue(orZero((currentMonthData.revenue / _thisMonthSoFarLength) * daysInMonth))
            setMonthProjectedAdspend(orZero((currentMonthData.adspend / _thisMonthSoFarLength) * daysInMonth))
            setMonthProjectedMer(currentMonthData.mer)
            setMonthProjectedCpp(currentMonthData.cpp)
            setMonthProjectedAov(currentMonthData.aov)

            setMonthRevenueSuggestedMax(findSuggestedMax(currentMonthData.revenueAvg, Math.max(...currentMonthData.revenueData), revenueGoal / daysInMonth, .1))
            setMonthAdspendSuggestedMax(findSuggestedMax(currentMonthData.adspendAvg, Math.max(...currentMonthData.adspendData), adspendGoal / daysInMonth, .2))
            setMonthMerSuggestedMax(findSuggestedMax(currentMonthData.merAvg, Math.max(...currentMonthData.merData), merGoal / daysInMonth, .1))
            setMonthCppSuggestedMax(findSuggestedMax(currentMonthData.cppAvg, Math.max(...currentMonthData.cppData), cppGoal / daysInMonth, .1))
            setMonthAovSuggestedMax(findSuggestedMax(currentMonthData.aovAvg, Math.max(...currentMonthData.aovData), aovGoal / daysInMonth, .1))

            setDataReady(true);
        }
    }, [processedData, currentMonthData]);

    // fetch for all date ranges in parallel
    const fetchAllData = async () => {
        log(`fetchAllData`, account);
        if (!account) return;
        setLoading(true);

        try {
            await Promise.all([
                fetchData('lastMonth',(r) => {
                    const _processedData = calculateMetricsAcrossDays(r.response);
                    setProcessedData(_processedData);
                },[ProviderNames.ADAYA]),
                fetchData('lastMonth',(r) => {
                    const _processedData = calculateMetricsAcrossDays(r.response);
                    setProcessedROASData(_processedData);
                }, [ProviderNames.SHOPIFY, ProviderNames.FACEBOOK]),
                fetchData('thisMonth',(r) => {
                    const _processedData = calculateMetricsAcrossDays(r.response);
                    setCurrentMonthData(_processedData);
                })
            ]);
        } catch(e) {
            log(`Exception fetching data`, e)
            setError(getInnerError(e));
        }

        console.log(`DONE`)
        setLoading(false);
    };

    // fetch for all date ranges in parallel
    const fetchData = async (range, cb, omitProviders?) => {
        try {
            const filter = getFilterFromSelection(range, omitProviders);
            // get both in parallel
            let r = await pageHelper.getDailyCampaignDataRequest(filter);
            onDataResponse(r, cb);
        } catch (e) {
            throw e;
        }
    }

    const onDataResponse = (r: ManagedRequest, cb) => {
        try {
            if (!r) throw "Response was empty.";

            if (r.status == RequestStatus.FINISHED) {
                cb(r)
            } else {
                // shouldn't get here, it's handled through exceptions mostly, but just in case.
                setError(getInnerError(r));
            }
        } catch (e) {
            log(`Exception`, e);
            throw e;
        }
    }

    // Goal needs all campaign data except Adaya
    const getFilterFromSelection = (range , omitProviders): CampaignDataFilter => {
        let timeRange = createIsoTimeRange(range);
        if (!timeRange) `A timeRange could not be created from the current selection: ${range}`;
        if (!account) throw `No account found for the current user.`;
        const providers = getAccountProviders(account, omitProviders || [ProviderNames.ADAYA]).map(p => p.name);
        return {
            account,
            timeRange,
            providers
        };
    }

    const onTabClick = (_tab) => {
        if (_tab != tab) {
            setPreviousTab(tab);
            setTab(_tab);
        }
    };

    const onCloseGoals = () => {
        setTab(previousTab || 'revenue');
        setPreviousTab('edit-goals');
        navigate('/goals');
    };

    // Todo: migrate to new data system/backend.
    const updateGoal = async (name, value) => {
        try {
            if (account) {
                if (!account.metadata) {
                    account.metadata = {
                        goals: GoalHelper.makeNewGoals()
                    };
                }

                account.metadata.goals[name] = value;
                let updatedAccount = await props.auth.updateAccount(account);
                // don't update the local account object, let useEffect [user] pull the change.

                if (updatedAccount) {
                    await props.auth.silentAuth();
                } else {
                    setError("Error updating account.");
                }
            }
        } catch (e) {
            setError(getInnerError(e));
        }
    };

    const renderTabAndGraph = (tabName, tabId, target, actual, projected, options) => {
        let msg = tabName + ' ';
        let msgs = tabId == 'cpp' ? cppMessages : messages;
        let dayIdx = moment().format('D') + 1;
        let percentageGoal = (((projected - target) / projected) * 100).toFixed(2);

        if (percentageGoal < -33) {
            let idx = dayIdx % msgs.wayDown.length;
            msg += msgs.wayDown[idx];
        } else if (percentageGoal >= -33 && percentageGoal < -10) {
            let idx = dayIdx % msgs.down.length;
            msg += msgs.down[idx];
        } else if (percentageGoal >= -10 && percentageGoal < 0) {
            let idx = dayIdx % msgs.slightlyDown.length;
            msg += msgs.slightlyDown[idx];
        } else if (percentageGoal < 10 && percentageGoal >= 0) {
            let idx = dayIdx % msgs.slightlyUp.length;
            msg += msgs.slightlyUp[idx];
        } else if (percentageGoal >= 10 && percentageGoal < 33) {
            let idx = dayIdx % msgs.up.length;
            msg += msgs.up[idx];
        } else if (percentageGoal >= 33) {
            let idx = dayIdx % msgs.wayUp.length;
            msg += msgs.wayUp[idx];
        } else {
            msg += msgs.recalculating;
        }

        let statusGood = (options && options.swapped) ? 'status-bad' : 'status-good';
        let statusBad = (options && options.swapped) ? 'status-good' : 'status-bad';

        return (
            <div className={`tab bullet-tab tab-${tabId} ` + (tab == tabId ? 'active' : '') + ' ' + (percentageGoal >= 0 ? statusGood : statusBad)}>
                <div className="label" onClick={() => onTabClick(tabId)}>
                    <div className="msg">{msg}</div>
                    <div className="arrow"></div>
                </div>
                <div className="tab-content">
                    {(tab == tabId) && dataReady &&

                        (tab == 'revenue') ?
                        <BarGraph
                            mobile
                            price
                            header={<div className="header">Daily<br />Revenue</div>}
                            name={"Revenue"}
                            key="revenue"
                            data={revenueData(revenueGoal, DAYS, daysInMonth)}
                            options={revenueOptions(monthRevenueSuggestedMax)}
                            target={revenueGoal}
                            dailyTarget={revenueGoal / daysInMonth}
                            actual={currentMonthData.revenue}
                            monthLength={daysInMonth}
                            projected={monthProjectedRevenue}
                            dataReady={dataReady}
                            threshold='0'
                            processedData={currentMonthData}
                            dataset={[...currentMonthData.revenueData]}
                        /> :

                        (tab == 'adspend') ?
                        <BarGraph
                            mobile
                            price
                            header={<div className="header">Ad<br />Spend</div>}
                            name={"Adspend"}
                            key="adspend"
                            data={adspendData(adspendGoal, DAYS, daysInMonth, adspendGoal / daysInMonth)}
                            options={adspendOptions(monthAdspendSuggestedMax)}
                            target={adspendGoal}
                            dailyTarget={adspendGoal / daysInMonth}
                            actual={currentMonthData.adspend}
                            monthLength={daysInMonth}
                            projected={monthProjectedAdspend}
                            dataReady={dataReady}
                            threshold='0'
                            processedData={currentMonthData}
                            dataset={[...currentMonthData.adspendData]}
                        /> :

                        (tab == 'roas') ?
                        <BarGraph
                            mobile
                            header={<div className="header">ROAS</div>}
                            name={"ROAS"}
                            key="roas"
                            data={roasData(merGoal, DAYS, daysInMonth)}
                            options={roasOptions(monthMerSuggestedMax)}
                            target={merGoal}
                            dailyTarget={merGoal / daysInMonth}
                            actual={(currentMonthData.mer)}
                            projected={monthProjectedMer}
                            monthLength={daysInMonth}
                            dataReady={dataReady}
                            threshold='0'
                            processedData={currentMonthData}
                            dataset={[...currentMonthData.merData]}
                        /> :

                        (tab == 'aov') ?
                        <BarGraph
                            mobile
                            price
                            header={<div className="header">AOV</div>}
                            name={"AOV"}
                            key="aov"
                            data={aovData(aovGoal, DAYS, daysInMonth)}
                            options={aovOptions(monthAovSuggestedMax)}
                            target={aovGoal}
                            dailyTarget={aovGoal / daysInMonth}
                            actual={currentMonthData.aov}
                            projected={monthProjectedAov}
                            monthLength={daysInMonth}
                            dataReady={dataReady}
                            threshold='0'
                            processedData={currentMonthData}
                            dataset={[...currentMonthData.aovData]}
                        /> :

                        (tab == 'cpp') ?
                        <BarGraph
                            mobile
                            price
                            header={<div className="header">Cost<br />Per Purchase</div>}
                            name={"CPP"}
                            key="cpp"
                            data={costPerPurchaseData((currentMonthData.totalAdspend / currentMonthData.totalOrders), DAYS, daysInMonth)}
                            options={costPerPurchaseOptions(monthCppSuggestedMax)}
                            target={cppGoal}
                            dailyTarget={cppGoal / daysInMonth}
                            actual={currentMonthData.cpp}
                            projected={monthProjectedCpp}
                            monthLength={daysInMonth}
                            dataReady={dataReady}
                            threshold='0'
                            swapped
                            processedData={currentMonthData}
                            dataset={[...currentMonthData.cppData]}
                        /> :

                        undefined
                    }
                </div>
            </div>
        )
    }

    return (
        <React.Fragment>

            <Helmet><title>{Config.SITE_TITLE_PREFIX} - Goals</title></Helmet>

            <div className="goals-mobile page-layout">
                {
                    (loading) ? renderLoader() :

                    (!loading && dataReady) && (

                        // error
                        error ? renderError(ErrorMessages.defaultErrorMessage(), error, "large")

                    // loaded ok
                    : (
                        <>
                            <div className={'cols flex-no-wrap ' + tab}>

                                <div className="col tabs">

                                    <div className={'tab edit-goals ' + (tab == 'edit-goals' ? 'active' : '')}>
                                        <div className="label">Your goal for {currentMonth} is {shortFormatCurrency(revenueGoal)}</div>
                                        <Button round filled color="green" onClick={() => onTabClick('edit-goals')}>Edit</Button>
                                    </div>

                                    {tab == 'edit-goals' &&
                                        <div className="edit-goals-container">
                                            <EditGoals
                                                auth={props.auth}
                                                data={props.deps}
                                                revenueGoal={editingRevenueGoal}
                                                cppGoal={editingCppGoal}
                                                aovGoal={editingAovGoal}
                                                merGoal={editingMerGoal}
                                                setRevenueGoal={(v) => { updateGoal('revenueGoal', v); setRevenueGoal(v) }}
                                                setCostPerPurchase={(v) => { updateGoal('cppGoal', v); setCppGoal(v); }}
                                                setAverageOrder={(v) => { updateGoal('aovGoal', v); setAovGoal(v); }}
                                                setMerGoal={(v) => { updateGoal('merGoal', v); setMerGoal(v); }}
                                                onClose={(e) => onCloseGoals()}
                                                mobile={true}
                                            />
                                        </div>
                                    }

                                    {renderTabAndGraph('Revenue', 'revenue', revenueGoal, currentMonthData.revenue, monthProjectedRevenue, { price: true })}

                                    {renderTabAndGraph('Adspend', 'adspend', adspendGoal, currentMonthData.adspend, monthProjectedAdspend, { price: true })}

                                    {renderTabAndGraph('ROAS', 'roas', roasGoal, currentMonthData.roas, monthProjectedMer, { price: false })}

                                    {renderTabAndGraph('AOV', 'aov', aovGoal, currentMonthData.aov, monthProjectedAov, { price: false })}

                                    {renderTabAndGraph('CPP', 'cpp', cppGoal, currentMonthData.cpp, monthProjectedCpp, { swapped: true })}

                                </div>
                            </div>
                        </>
                    ))
                }
            </div>

        </React.Fragment>
    )
}

export default GoalMobile;